import { Anchor, Button, Checkbox, Select, Text } from '@mantine/core';
import { useField } from '@mantine/form';
import { modals } from '@mantine/modals';
import { IconScale, IconSend } from '@tabler/icons-react';
import { ChooseAdditionalInvoices } from 'app/Private/Clients/Detail/UnhandledInvoicesTab/ChooseAdditionalInvoices';
import { useInvoiceSelection } from 'app/Private/Clients/Detail/UnhandledInvoicesTab/use-invoice-selection';
import { iconProps } from 'components/icon-props';
import { Right } from 'components/Right';
import { tKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import type { InvoiceAction, UnhandledInvoice } from 'modules/AtRisk/api/use-load-unhandled-client';
import { useState } from 'react';
import { isNullish } from 'remeda';
import { useLoadThirdParties } from 'shared/hooks/use-load-third-parties';
import { isProduction } from 'shared/utils/environment';
import { config } from 'store/constants';
import type { ActionButtonDefinition } from '../use-build-action-buttons';
import { computeCommonProps } from './common';
import { useThirdPartySend } from './use-third-party-send';

export function useBailiff(): (
  action: InvoiceAction,
  invoices: Array<UnhandledInvoice>,
) => ActionButtonDefinition {
  const { t } = useTranslation();
  const { selectedInvoiceIds } = useInvoiceSelection();

  return (action, invoices) => {
    const handler = () =>
      modals.open({
        size: 'lg',
        title: t(tKeys.DEBTOR.REACTION.SELECT_INVOICES),
        children: (
          <ChooseAdditionalInvoices
            selectedInvoiceIds={selectedInvoiceIds}
            unhandledInvoices={invoices}
            actionType="bailiff"
            onNext={({ invoiceIds }) => {
              const unhandledIds = new Set(invoices.map(invoice => invoice.id));
              modals.closeAll();
              modals.open({
                size: 'auto',
                title: t(tKeys.CHOOSE_BAILIFF),
                children: (
                  <ChooseBailiff
                    invoices={invoices.filter(invoice => invoiceIds.includes(invoice.id))}
                    extraInvoiceIds={invoiceIds.filter(id => !unhandledIds.has(id))}
                  />
                ),
              });
            }}
          />
        ),
      });

    return {
      icon: <IconScale {...iconProps()} />,
      translationKey: tKeys.THIRD_PARTY_CASES.CASE_TYPE.BAILIFF,
      onClick: handler,
      ...computeCommonProps(action, invoices),
    };
  };
}

type ChooseBailiffProps = {
  invoices: Array<UnhandledInvoice>;
  extraInvoiceIds: Array<string>;
};

const ChooseBailiff = ({ invoices, extraInvoiceIds }: ChooseBailiffProps) => {
  const { t, currentLang } = useTranslation();

  const { invoiceIds, isSendToThirdPartyLoading, send } = useThirdPartySend(
    invoices,
    extraInvoiceIds,
  );
  const {
    thirdParties: { bailiff },
    isThirdPartiesInitialLoading,
  } = useLoadThirdParties({ ids: invoiceIds.map(Number) });

  const selectedBailiff = useField<string | null>({ initialValue: '' });
  const selectedBailiffId = selectedBailiff.getValue();
  const uncontested = useField<boolean>({ initialValue: false });
  const generalConditionsApproved = useField<boolean>({ initialValue: false });
  const [isConfirmingContested, setIsConfirmingContested] = useState(false);

  const bailiffTerms = config.bailiffTerms as Record<number, Record<string, string>>;
  const generalConditionsUrl = isProduction()
    ? (bailiffTerms[Number(selectedBailiffId)]?.[currentLang] ?? '')
    : '';

  const sendToBailiff = () => {
    if (isNullish(selectedBailiffId)) return;
    send(selectedBailiffId, { contested: !uncontested.getValue() });
  };

  const submit = () => {
    if (uncontested.getValue()) {
      sendToBailiff();
      return;
    }
    setIsConfirmingContested(true);
  };

  const confirmContestedSend = () => {
    setIsConfirmingContested(false);
    sendToBailiff();
  };

  if (isThirdPartiesInitialLoading) return <div>{t(tKeys.LOADING)}</div>;

  return (
    <>
      <Select
        label={t(tKeys.SELECT_BAILIFF)}
        placeholder={t(tKeys.SELECT_BAILIFF)}
        data={bailiff.availableCompanies.map(company => ({
          label: company.name,
          value: String(company.id),
        }))}
        {...selectedBailiff.getInputProps()}
      />
      <Checkbox
        mt="md"
        label={t(tKeys.FORM.CONFIRM_BAILIFF.INVOICE_UNCONTESTABLE)}
        checked={uncontested.getValue()}
        onChange={event => uncontested.setValue(event.currentTarget.checked)}
      />
      <Checkbox
        mt="md"
        label={
          <>
            {t(tKeys.FORM.CONFIRM_BAILIFF.READ_AND_APPROVE)}{' '}
            <Anchor href={generalConditionsUrl} target="_blank" rel="noopener noreferrer">
              {t(tKeys.FORM.GENERAL_CONDITIONS_OF_USE)}
            </Anchor>
          </>
        }
        checked={generalConditionsApproved.getValue()}
        onChange={event => generalConditionsApproved.setValue(event.currentTarget.checked)}
      />
      {isConfirmingContested ? (
        <>
          <Text mt="md">{t(tKeys.FORM.CONFIRM_BAILIFF.AYS_SEND_CONTESTED_INVOICE)}</Text>
          <Right mt="md">
            <Button variant="default" onClick={() => setIsConfirmingContested(false)}>
              {t(tKeys.CANCEL)}
            </Button>
            <Button
              leftSection={<IconSend {...iconProps()} />}
              loading={isSendToThirdPartyLoading}
              onClick={confirmContestedSend}
            >
              {t(tKeys.CONFIRM)}
            </Button>
          </Right>
        </>
      ) : (
        <Right mt="md">
          <Button
            leftSection={<IconSend {...iconProps()} />}
            loading={isSendToThirdPartyLoading}
            disabled={selectedBailiff.getValue() === '' || !generalConditionsApproved.getValue()}
            onClick={submit}
          >
            {t(tKeys.SUBMIT)}
          </Button>
        </Right>
      )}
    </>
  );
};
